import { useState, useEffect } from "react";
import { createPortal } from "react-dom";

import { TOUR_STEPS, type TourStepId } from "@optidev/shared-types";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { useEditorTour } from "@/hooks/use-editor-tour";
import {
  readAnchorRect,
  sameAnchorRect,
  placeTourCard,
  type AnchorRect,
} from "@/lib/tour/geometry";
import { resolveTourAnchor } from "@/stores/tour-store";

/** Which side of its anchor a step's card sits on. */
type Side = "right" | "left" | "below";

interface StepCopy {
  id: TourStepId;
  title: string;
  body: string;
  side: Side;
}

/**
 * Copy is deliberately conditional. We cannot know whether an app is finished, so
 * nothing here claims it is ready, or tells the user to act now. Each line says
 * where a control is and what it does, and leaves the timing to the person who
 * actually knows.
 */
const STEP_COPY: Record<TourStepId, StepCopy> = {
  composer: {
    id: "composer",
    title: "Ask for changes here",
    body: "Describe what you want in plain English. OptiDev edits the code for you.",
    side: "right",
  },
  preview: {
    id: "preview",
    title: "Your app appears here",
    body: "It reloads every time a change lands.",
    side: "left",
  },
  publish: {
    id: "publish",
    title: "When your app is ready, publish it from here",
    body: "You'll get a link you can share or open on any screen.",
    side: "below",
  },
};

const CARD_WIDTH = 288;
const GAP = 14;
const EDGE_PAD = 12;
/** Fallback while the card's real height is unknown on the first paint. */
const ASSUMED_CARD_HEIGHT = 150;

export function EditorTour() {
  const { isActive, step, stepCount, isAnchorReady, next, back, skip } =
    useEditorTour();
  const [rect, setRect] = useState<AnchorRect | null>(null);
  const [cardHeight, setCardHeight] = useState(0);

  const stepId = TOUR_STEPS[step];
  const copy = STEP_COPY[stepId];
  const isLive = isActive && isAnchorReady;

  /*
    Track the anchor every frame while a step is up.

    Event listeners alone are not enough to keep the cutout exact. resize and
    scroll miss the common case: the anchor keeps its size but *moves*, because a
    sibling grew. The chat transcript growing as the agent streams pushes the
    composer down; the Present bar appearing shifts the preview. A ResizeObserver
    on the anchor does not fire for that, and one on document.body only fires when
    the page itself resizes, so the spotlight would sit visibly off its target.

    Reading one rect per frame and committing state only when it actually changed
    is cheap, and it is the only approach that stays exact through animated layout
    (panel drags, the transform wrapper settling) rather than just after it.
  */
  useEffect(() => {
    if (!isLive) {
      setRect(null);
      return;
    }

    let frame = 0;
    let current: AnchorRect | null = null;

    const tick = () => {
      const anchor = resolveTourAnchor(stepId);
      const nextRect = anchor ? readAnchorRect(anchor) : null;
      if (!sameAnchorRect(current, nextRect)) {
        current = nextRect;
        setRect(nextRect);
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [isLive, stepId]);

  useEffect(() => {
    if (!isLive) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        skip();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isLive, skip]);

  if (!isLive || !rect) return null;

  const isLast = step === stepCount - 1;
  const h = cardHeight || ASSUMED_CARD_HEIGHT;
  const { top, left } = placeTourCard(
    rect,
    copy.side,
    CARD_WIDTH,
    h,
    window.innerWidth,
    window.innerHeight,
    GAP,
    EDGE_PAD,
  );

  return createPortal(
    <>
      {/*
        Spotlight instead of a coloured outline. A ring around the control read as
        a validation error and fought the app's own focus styling; dimming
        everything else says "look here" without adding a colour to the UI.

        One element does it: a box sized to the anchor whose box-shadow spreads far
        enough to cover the viewport, leaving the anchor as a lit cutout.

        The cutout is the element's own border box, so it equals the anchor rect
        exactly. Two consequences, both deliberate:

        - The hairline is `inset`. An outward 1px shadow would paint over the scrim
          just outside the box, making the lit area read 1px larger than the control.
        - No transition on the geometry. Tracking runs every frame, so easing the
          box would leave it chasing a moving anchor and visibly out of register.
          The card below still eases, because it is not a cutout and lag there
          reads as motion rather than misalignment.
      */}
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none fixed z-[9996]",
          "[--tour-scrim:rgb(9_9_11/0.42)] dark:[--tour-scrim:rgb(0_0_0/0.62)]",
        )}
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          borderRadius: rect.radius,
          boxShadow:
            "inset 0 0 0 1px rgb(255 255 255 / 0.22), 0 0 0 9999px var(--tour-scrim)",
        }}
      />

      <div
        role="dialog"
        aria-live="polite"
        aria-label={`Tour step ${step + 1} of ${stepCount}`}
        className={cn(
          "fixed z-[9998] rounded-md border border-border bg-popover p-4",
          "text-popover-foreground shadow-lg",
          "transition-[top,left] duration-300 ease-out motion-reduce:transition-none",
        )}
        style={{ top, left, width: CARD_WIDTH }}
        ref={(el) => {
          if (el && el.offsetHeight !== cardHeight) setCardHeight(el.offsetHeight);
        }}
      >
        <p className="text-sm font-semibold leading-snug">{copy.title}</p>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
          {copy.body}
        </p>

        <div className="mt-4 flex items-center gap-2">
          <span className="mr-auto font-mono text-xs text-muted-foreground">
            {step + 1} of {stepCount}
          </span>

          {step > 0 && (
            <Button variant="ghost" size="sm" onClick={back} className="h-7 px-2">
              Back
            </Button>
          )}
          {/* No Skip on the last step: the primary action already ends the tour,
              so offering both is two buttons for one outcome. */}
          {!isLast && (
            <Button variant="ghost" size="sm" onClick={skip} className="h-7 px-2">
              {step === 0 ? "Skip tour" : "Skip"}
            </Button>
          )}
          <Button size="sm" onClick={next} className="h-7 px-3">
            {isLast ? "Got it" : "Next"}
          </Button>
        </div>
      </div>
    </>,
    document.body,
  );
}
